'use client'

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react'

const schema = z.object({
  email:    z.string().email('Voer een geldig e-mailadres in'),
  password: z.string().min(1, 'Voer je wachtwoord in'),
})

type FormData = z.infer<typeof schema>

export default function InloggenPage() {
  const router = useRouter()
  const [error, setError]           = useState<string | null>(null)
  const [loading, setLoading]       = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  /* Gezet door /account/wachtwoord-wijzigen na een geslaagde wijziging. */
  const [passwordChanged, setPasswordChanged] = useState(false)
  useEffect(() => {
    setPasswordChanged(
      new URLSearchParams(window.location.search).get('wachtwoord') === 'gewijzigd',
    )
  }, [])

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    setError(null)
    try {
      const result = await signIn('credentials', {
        email:    data.email,
        password: data.password,
        redirect: false,
      })
      if (result?.error) {
        setError('Onjuist e-mailadres of wachtwoord. Probeer het opnieuw.')
      } else {
        // Return to wherever the user was headed before being sent to login
        // (e.g. the dev lockdown / admin gate set ?callbackUrl=). Only honour
        // safe, in-app relative paths; otherwise fall back to the account page.
        const cb = new URLSearchParams(window.location.search).get('callbackUrl')
        const dest = cb && cb.startsWith('/') && !cb.startsWith('//') ? cb : '/account'
        router.push(dest)
        router.refresh()
      }
    } catch {
      setError('Er is iets misgegaan. Probeer het later opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-16"
      style={{
        backgroundColor: '#FDF6EC',
        backgroundImage: `radial-gradient(circle at 20% 50%, rgba(139,69,19,0.06) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(218,165,32,0.08) 0%, transparent 40%),
          radial-gradient(circle at 60% 80%, rgba(210,105,30,0.05) 0%, transparent 40%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        {/* Card */}
        <div
          className="rounded-3xl p-8 sm:p-10"
          style={{
            background: '#fff',
            boxShadow: '0 20px 60px rgba(139,69,19,0.15), 0 4px 20px rgba(139,69,19,0.08)',
          }}
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{ background: 'linear-gradient(135deg, #8B4513, #D2691E)' }}
            >
              <span className="text-2xl">🥖</span>
            </div>
            <h1
              className="text-2xl font-bold tracking-tight"
              style={{ fontFamily: 'var(--font-dancing)', color: '#8B4513', fontSize: '1.6rem' }}
            >
              Bakkerij Voortman
            </h1>
            <p style={{ color: '#D2691E', fontSize: '0.82rem', fontStyle: 'italic' }}>
              Al sinds 1946 · vers &amp; ambachtelijk
            </p>
          </div>

          {/* Title */}
          <h2
            className="text-3xl font-bold text-center mb-8"
            style={{ fontFamily: 'var(--font-playfair)', color: '#2C1810' }}
          >
            Welkom terug
          </h2>

          {/* Error */}
          {/* Wie zojuist een nieuw wachtwoord koos, komt hier uit: dat beëindigt
              alle openstaande sessies, dus ook de zijne. Zonder uitleg lijkt dat
              op een fout. */}
          {passwordChanged && !error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 px-4 py-3 rounded-xl text-sm font-medium"
              style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}
            >
              Uw wachtwoord is gewijzigd. Log opnieuw in met uw nieuwe wachtwoord.
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 px-4 py-3 rounded-xl text-sm font-medium"
              style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold mb-1.5"
                style={{ color: '#2C1810' }}
              >
                E-mailadres
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: '#8B4513' }}
                />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="uw@email.nl"
                  {...register('email')}
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm transition-all outline-none"
                  style={{
                    background: '#FDF6EC',
                    border: errors.email ? '1.5px solid #DC2626' : '1.5px solid #E8D5C4',
                    color: '#2C1810',
                  }}
                  onFocus={(e) => (e.target.style.border = '1.5px solid #8B4513')}
                  onBlur={(e) =>
                    (e.target.style.border = errors.email
                      ? '1.5px solid #DC2626'
                      : '1.5px solid #E8D5C4')
                  }
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs" style={{ color: '#DC2626' }}>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            {/* The "Wachtwoord vergeten?" link is rendered *after* the input in
               the DOM (and pinned top-right via absolute positioning) so Tab
               from the e-mail field lands on the password field, not the link. */}
            <div className="relative">
              <label
                htmlFor="password"
                className="block text-sm font-semibold mb-1.5"
                style={{ color: '#2C1810' }}
              >
                Wachtwoord
              </label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: '#8B4513' }}
                />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Je wachtwoord"
                  {...register('password')}
                  className="w-full pl-10 pr-11 py-3 rounded-xl text-sm transition-all outline-none"
                  style={{
                    background: '#FDF6EC',
                    border: errors.password ? '1.5px solid #DC2626' : '1.5px solid #E8D5C4',
                    color: '#2C1810',
                  }}
                  onFocus={(e) => (e.target.style.border = '1.5px solid #8B4513')}
                  onBlur={(e) =>
                    (e.target.style.border = errors.password
                      ? '1.5px solid #DC2626'
                      : '1.5px solid #E8D5C4')
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: '#8B4513' }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <Link
                href="/wachtwoord-vergeten"
                className="absolute top-0 right-0 text-xs font-medium hover:underline"
                style={{ color: '#8B4513' }}
              >
                Wachtwoord vergeten?
              </Link>
              {errors.password && (
                <p className="mt-1 text-xs" style={{ color: '#DC2626' }}>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              style={{
                background: loading
                  ? '#C4956A'
                  : 'linear-gradient(135deg, #8B4513, #D2691E)',
                color: '#fff',
                boxShadow: '0 4px 14px rgba(139,69,19,0.35)',
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Even geduld…
                </>
              ) : (
                'Inloggen'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6 flex items-center">
            <div className="flex-1 h-px" style={{ background: '#E8D5C4' }} />
            <span className="px-3 text-sm" style={{ color: '#A07850' }}>
              of
            </span>
            <div className="flex-1 h-px" style={{ background: '#E8D5C4' }} />
          </div>

          {/* Register link */}
          <p className="text-center text-sm" style={{ color: '#6B4226' }}>
            Nog geen account?{' '}
            <Link
              href="/registreren"
              className="font-semibold hover:underline"
              style={{ color: '#8B4513' }}
            >
              Registreer je hier
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  )
}
